import React, { useRef, useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import dhyanaService from "../services/dhyanaService";
import { trackEvent } from "../constants";

// Types

interface AIContent {
  headline: string;
  content: string;
  cta: string;
  stage?: number;
  personaKey?: string;
  source?: string;
  fallback?: boolean;
  fallbackReason?: string;
  generated_at?: string;
}

interface ContentBlockProps {
  stage: number; // Stage number (1–14)
  personaKey: string; // Persona key (e.g. "high_business")
  personaName?: string; // Optional, only for display
  onVisible: (stage: number) => void;
  stageInfo?: { name: string; type: string }; // Optional stage info from parent
}

const ContentBlock: React.FC<ContentBlockProps> = ({
  stage,
  personaKey,
  personaName = "Professional",
  onVisible,
  stageInfo,
}) => {
  const blockRef = useRef<HTMLDivElement | null>(null);
  const [content, setContent] = useState<AIContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localStageInfo, setLocalStageInfo] = useState<{
    name: string;
    type: string;
  } | null>(stageInfo || null);

  // Load stage info from API if not provided
  useEffect(() => {
    if (!stageInfo && !localStageInfo) {
      dhyanaService
        .loadConfig()
        .then((config) => {
          if (config.stages && config.stages[stage]) {
            const stageData: any = config.stages[stage];
            const stageName =
              typeof stageData === "object" && stageData && stageData.name
                ? stageData.name
                : typeof stageData === "string"
                ? stageData
                : `Stage ${stage}`;
            const stageType =
              typeof stageData === "object" && stageData && stageData.type
                ? stageData.type
                : "informational";
            setLocalStageInfo({
              name: stageName,
              type: stageType,
            });
          } else {
            setLocalStageInfo({
              name: `Stage ${stage}`,
              type: "informational",
            });
          }
        })
        .catch((err) => {
          console.error("Failed to load stage info:", err);
          setLocalStageInfo({
            name: `Stage ${stage}`,
            type: "informational",
          });
        });
    }
  }, [stage, stageInfo, localStageInfo]);

  // Load AI content when component mounts
  useEffect(() => {
    let cancelled = false;

    const loadAIContent = async () => {
      const startTime = Date.now();

      try {
        setLoading(true);
        setError(null);

        console.log(
          `Loading AI content for stage ${stage}, persona ${personaKey}`,
        );

        // Generate AI content from Dhyana Brain via dhyanaService
        const aiContent = await dhyanaService.generateLandingContent(
          stage,
          personaKey,
        );

        if (cancelled) return;

        const generationTime = Date.now() - startTime;

        setContent(aiContent as AIContent);

        // Track successful AI content generation
        trackEvent("ai_content_loaded", {
          stage_number: stage,
          stage_name: (stageInfo || localStageInfo)?.name || `Stage ${stage}`,
          persona_key: personaKey,
          persona_name: personaName,
          generation_time_ms: generationTime,
          fallback_used: aiContent.fallback || false,
          fallback_reason: (aiContent as any).fallbackReason || null,
          content_source: aiContent.source || "unknown",
        });

        console.log(`AI content loaded successfully in ${generationTime}ms:`, {
          headline: aiContent.headline,
          content: aiContent.content,
          cta: aiContent.cta,
          fallback: aiContent.fallback,
        });
      } catch (err) {
        if (cancelled) return;

        console.error("AI Content Generation Error:", err);
        setError(err instanceof Error ? err.message : "Unknown error");

        // API-only architecture - no fallbacks allowed
        setError(err instanceof Error ? err.message : "Unknown error");

        // Track fallback usage
        trackEvent("ai_content_fallback", {
          stage_number: stage,
          persona_key: personaKey,
          error_type: err instanceof Error ? err.name : "unknown_error",
          error_message: err instanceof Error ? err.message : "Unknown error",
        });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAIContent();

    return () => {
      cancelled = true;
    };
  }, [stage, personaKey, personaName, stageInfo, localStageInfo]);

  // Intersection observer for visibility tracking
  useEffect(() => {
    const node = blockRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onVisible(stage);

            // Track content block visibility
            trackEvent("content_block_viewed", {
              stage_number: stage,
              stage_name:
                (stageInfo || localStageInfo)?.name || `Stage ${stage}`,
              persona_type: personaKey,
              persona_name: personaName,
              ai_generated: content && !content.fallback,
              fallback_used: content?.fallback || false,
            });
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: "0px 0px -100px 0px", // Trigger slightly before coming into view
      },
    );

    observer.observe(node);

    return () => {
      observer.unobserve(node);
      observer.disconnect();
    };
  }, [
    onVisible,
    stage,
    personaKey,
    personaName,
    content,
    stageInfo,
    localStageInfo,
  ]);

  const currentStageInfo = stageInfo ||
    localStageInfo || { name: `Stage ${stage}`, type: "informational" };

  return (
    <section
      ref={blockRef}
      className="py-20 px-6 flex items-center justify-center bg-white"
      style={{ minHeight: "90vh" }}
    >
      <div className="max-w-4xl w-full">
        {/* Clean stage indicator */}
        <div className="flex items-center justify-center mb-12">
          <div className="flex items-center space-x-4">
            <span className="w-12 h-12 bg-slate-900 text-white rounded-full flex items-center justify-center text-lg font-medium">
              {stage}
            </span>
            <div className="flex flex-col">
              <span className="text-slate-500 text-sm font-medium">
                Stage {stage}
              </span>
              <span className="text-slate-900 text-lg font-semibold">
                {currentStageInfo.name}
              </span>
            </div>
            {content?.source === "ai_generated" && (
              <span className="px-3 py-1 text-xs bg-slate-100 text-slate-600 rounded-full font-medium">
                AI Generated
              </span>
            )}
          </div>
        </div>

        {/* Clean content area */}
        <article className="bg-white rounded-lg border border-slate-200 p-8 lg:p-12 relative">
          {/* Clean loading state */}
          {loading && (
            <div className="text-center py-16">
              <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin mx-auto mb-6"></div>
              <div className="space-y-3 max-w-lg mx-auto">
                <div className="h-6 bg-slate-100 rounded w-3/4 mx-auto"></div>
                <div className="h-4 bg-slate-100 rounded w-full"></div>
                <div className="h-4 bg-slate-100 rounded w-5/6 mx-auto"></div>
              </div>
              <p className="text-slate-500 mt-6 text-sm">Loading content...</p>
            </div>
          )}

          {/* Clean error state */}
          {error && !content && (
            <div className="text-center py-16">
              <div className="w-12 h-12 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                Content unavailable
              </h3>
              <p className="text-slate-500 text-sm mb-6">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="bg-slate-900 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Clean content display */}
          {content && !loading && (
            <>
              <h2 className="text-2xl lg:text-4xl font-semibold text-slate-900 mb-6 leading-tight">
                {content.headline}
              </h2>
              <div className="mb-8">
                <p className="text-lg text-slate-600 leading-relaxed">
                  {content.content}
                </p>
              </div>

              {/* Clean, unified CTA */}
              <div className="border-t border-slate-200 pt-8">
                <div className="text-center">
                  <button className="bg-slate-900 text-white px-8 py-3 rounded-md font-medium hover:bg-slate-800 transition-colors">
                    {content.cta || "Learn More"}
                  </button>
                  <p className="text-slate-500 text-sm mt-3">
                    {personaKey.startsWith("high") &&
                      "Executive insights delivered weekly"}
                    {personaKey.includes("technical") &&
                      "Technical resources for leaders"}
                    {!personaKey.startsWith("high") &&
                      !personaKey.includes("technical") &&
                      "Strategic marketing intelligence"}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Clean continuation indicator */}
          {content && !loading && (
            <div className="flex justify-center mt-12" aria-hidden="true">
              <ChevronDown className="w-6 h-6 text-slate-300" />
            </div>
          )}
        </article>
      </div>
    </section>
  );
};

export default ContentBlock;
